import { type FC } from "react";
import { useRouter } from "next/router";
import { server } from "../../state";
import {
  Dashboard,
  DocumentExtraction,
  DownloadMenu,
  Email,
  Folder,
  HumanReview,
  Library,
  SensibleLogo,
} from "./icons";
import styles from "./Navbar.module.css";
import { Link } from "./design-system/Link";

const USAGE_LIMIT_FREE = 150;

const NavigationTitle = ({
  label,
  className,
}: {
  label: string;
  className?: string;
}) => <div className={`${styles.title} ${className}`}>{label}</div>;

const NavigationItem = ({
  icon,
  active,
  label,
  href,
  className,
  onClick,
}: {
  href: string;
  icon?: JSX.Element;
  label: string;
  active?: boolean;
  className?: string;
  onClick?: () => void;
}) => {
  return (
    <li
      onClick={() => onClick?.()}
      className={`${styles.navigationItemContainer} ${className}`}
    >
      <Link
        href={href}
        className={`${styles.navigationItem} ${
          active ? styles.active : styles.inactive
        }`}
      >
        <div className={styles.navigationItemIcon}>{icon}</div>
        <span>{label}</span>
      </Link>
    </li>
  );
};

interface Props {
  isSidebarOpen: boolean;
}

const Sidebar: FC<Props> = ({ isSidebarOpen }) => {
  const account = server.useAccount();
  const router = useRouter();

  const OverageAlert = () => {
    if (account) {
      if (
        account.plan === server.Subscription.Free &&
        account.usage.total >= USAGE_LIMIT_FREE
      ) {
        return (
          <div className="flex flex-col justify-center bg-brand-200 mb-2">
            <div className="text font-semibold px-4 pt-4">
              Free plan overage
            </div>
            <div className="text-sm p-4">
              {`You've`} hit your extraction limit for the month. Upgrade on the{" "}
              <Link href="/account/">account page</Link> to continue extracting
              data from your documents.
            </div>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <nav
      className={`${styles.nav} ${isSidebarOpen ? styles.open : styles.closed}`}
    >
      <div className="flex flex-col">
        <div className={`${styles.logoContainer}`}>
          <Link href="/" title="home" className={styles.logo}>
            <SensibleLogo />
          </Link>
        </div>
        <ul className="py-2 pb-8">
          <NavigationItem
            label={"Dashboard"}
            href={"/dashboard"}
            active={router.pathname.includes("dashboard")}
            icon={<Dashboard />}
          />
          <NavigationTitle className={"mt-ds-6 mb-ds-1"} label={"CONFIGURE"} />
          <NavigationItem
            label={"Document types"}
            href={"/document-types"}
            active={router.pathname.includes("document-types")}
            icon={<Folder />}
          />
          {account?.aliases?.length ? (
            <NavigationItem
              label={"Email processors"}
              href={"/email-processors"}
              active={router.pathname.includes("email-processors")}
              icon={<Email />}
            />
          ) : null}
          <NavigationItem
            label={"Template library"}
            href={"/library"}
            active={router.pathname.includes("library")}
            icon={<Library />}
          />
          <NavigationTitle className={"mt-ds-6 mb-ds-1"} label={"MONITOR"} />
          <NavigationItem
            href={"/extractions"}
            label={"Extraction history"}
            icon={<DocumentExtraction />}
            active={router.pathname.includes("/extraction")}
          />
          <NavigationItem
            href={"/human-review"}
            label={"Human review"}
            icon={<HumanReview />}
            active={router.pathname.includes("/human-review")}
          />
          <NavigationTitle className={"mt-ds-6 mb-ds-1"} label={"EXTRACT"} />
          <NavigationItem
            href={"/quick-extraction"}
            label={"Extract"}
            icon={<DownloadMenu />}
            active={router.pathname.includes("quick-extraction")}
          />
        </ul>
      </div>
      <div>
        <OverageAlert />
      </div>
    </nav>
  );
};

export default Sidebar;
